import { Badge, Card, Icon, IconProps } from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';
import { Fragment, memo, ReactNode, useEffect, useState } from 'react';
import { StyledSideNavPolarisLayout } from './styled';
import SelectedTabArrow from '@/asset/icons/SelectedTabArrow';
import VerticalLine from '@/asset/icons/VerticalLine';
import HoverTabArrow from '@/asset/icons/HoverTabArrow';

interface SideNavPolarisLayoutProps {
  tabsConfig: Array<IPolarisSideNavTabs>;
  selectedTab: string;
  selectedSubTab?: string;
  onLeave: (tab: string, subTab?: string) => void;
  navResponsiveWidth?: string;
  isChangeData: boolean;
}

interface IPolarisSideNavTabs {
  value: string;
  label: ReactNode;
  component?: ReactNode | JSX.Element;
  icon?: IconProps['source'];
  hideControlBar?: boolean;
  subTab?: Array<Omit<IPolarisSideNavTabs, 'subTab'>>;
  isOpenSubTab?: boolean;
  badge?: string;
  onClick?: () => void;
}

const SideNavMenu = ({
  tabsConfig,
  selectedTab,
  selectedSubTab,
  onLeave,
  navResponsiveWidth = '1024px',
  isChangeData,
}: SideNavPolarisLayoutProps) => {
  const selectedTabConfig = tabsConfig.find((tab) => tab.value === selectedTab);
  const [changedOption, setChangeOption] = useState<{
    tab: string;
    subTab?: string;
  }>({ tab: '' });

  const renderSubTabsSelectedSvgIcon = (tab: IPolarisSideNavTabs, valueSubTab: string) => {
    if (selectedTabConfig && selectedTabConfig.subTab && selectedSubTab) {
      const selectedSubTabIndex = selectedTabConfig.subTab.findIndex((item) => item.value === selectedSubTab);
      const currentSubTabIndex = selectedTabConfig.subTab.findIndex((item) => item.value === valueSubTab);
      if (valueSubTab === selectedSubTab) {
        return <SelectedTabArrow />;
      }
      if (currentSubTabIndex < selectedSubTabIndex) {
        return <VerticalLine />;
      }
    }
    if (selectedTabConfig && selectedTabConfig.value !== tab.value && tab.isOpenSubTab) {
      return <VerticalLine />;
    }
    return undefined;
  };

  const renderSubTabsHoverSvgIcon = (value: string) => {
    if (selectedTabConfig) {
      if (value !== selectedSubTab) {
        return <HoverTabArrow />;
      }
    }
    return undefined;
  };

  const resetData = () => {
    if (changedOption.tab) {
      setChangeOption({ tab: '' });
    }
  };

  const handleChangeEvent = (tab: string, subTab?: string) => {
    if (tab === selectedTab && (!subTab || subTab === selectedSubTab)) {
      return;
    }

    if (isChangeData) {
      setChangeOption({ tab, subTab });
      return;
    }
    resetData();
    onLeave(tab, subTab);
  };

  // useEffect(() => {
  //   if (selectedTab || selectedSubTab) {
  //     window.scrollTo(0, 0);
  //   }
  // }, [selectedTab, selectedSubTab]);

  useEffect(() => {
    if (!isChangeData) {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChangeData]);

  return (
    <>
      <StyledSideNavPolarisLayout $navResponsiveWidth={navResponsiveWidth}>
        <div className="page-tabs">
          <Card padding="200">
            {tabsConfig.map((tab, index) => (
              <Fragment key={index}>
                <div
                  className={`tabs ${tab.value === selectedTab && !tab.subTab ? 'selected' : ''} ${
                    tab.subTab ? 'non-clickable-tab' : ''
                  }`}
                  onClick={() => {
                    tab.onClick?.();
                    handleChangeEvent(tab.value);
                  }}
                >
                  {tab.value === selectedTab && tab.subTab && (
                    <div className="line__icon">
                      <VerticalLine />
                    </div>
                  )}
                  {tab.icon && <Icon source={tab.icon} />}
                  <div className="tabs__label">
                    {tab.label}
                    {tab.badge ? (
                      <Badge tone="warning" size="small">
                        {tab.badge}
                      </Badge>
                    ) : (
                      <div className="tick__icon">
                        <Icon source={CheckIcon} />
                      </div>
                    )}
                  </div>
                </div>

                {tab.subTab &&
                  (tab.isOpenSubTab || tab.value === selectedTab) &&
                  tab.subTab.map((sub, index) => (
                    <Fragment key={index}>
                      <div
                        className={`sub-tabs ${sub.value === selectedSubTab ? 'selected' : ''}`}
                        onClick={() => {
                          sub.onClick?.();
                          handleChangeEvent(tab.value, sub.value);
                        }}
                      >
                        <div className="sub-tabs__svg-selected">{renderSubTabsSelectedSvgIcon(tab, sub.value)}</div>
                        <div className="sub-tabs__svg-hover">{renderSubTabsHoverSvgIcon(sub.value)}</div>
                        <div className="sub-tabs__label">
                          {sub.label}
                          <div className="tick__icon">
                            <Icon source={CheckIcon} />
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  ))}
              </Fragment>
            ))}
          </Card>
        </div>
        <div className="page-content" />
      </StyledSideNavPolarisLayout>
    </>
  );
};

export default memo(SideNavMenu);
